import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem, Select, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import api from "../../common/api";
import { formatDateTime } from "../../common/common";
import { loadScans } from "../../reducer/ScansSlice";
import "./DataWatcher.css";
import { resetData } from "../../reducer/LaserDataSlice";
import { uploadShow } from "../../reducer/ScanUploadSlice";
import ScanUpload from "../ScanUpload";
import { hideProcessing, showProcessing } from "../../reducer/ProcessingSlice";
import { loadCaptureScans } from "../../reducer/RawSurfaceSlice";
import { useAuth0 } from "@auth0/auth0-react";
import { tokens } from "../../theme";

let interval = -1;

/** Matches the mockup's field label: small, uppercase, letter-spaced, muted. */
const fieldLabelSx = {
    fontSize: "10.5px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: tokens.muted,
    marginBottom: "3px",
};

/**
 * The AU/Scan selects' own rendered height (their closed box, driven by twoLineValue's reserved
 * two-line height under the current theme) - not the label above them. Delete Scan / Upload Scan
 * use this to center against the dropdown itself, matching under a hidden label spacer of their
 * own so the button still starts at the same row position the dropdowns do.
 */
const FIELD_HEIGHT = "58px";

/**
 * A Select's closed-box display always reserves the same two-line height - via an invisible
 * ghost copy of that shape - so the toolbar's height never changes between "nothing selected"
 * and "something selected", and the Alignment Unit select (which has no secondary line of its
 * own) still comes out exactly as tall as the Scan/Raw Capture selects next to it.
 *
 * A single-line value (no secondary) is centered across that full reserved height rather than
 * pinned to the top - plain flow layout can't do that (the ghost line already fills the height,
 * leaving no free space for a flex/Stack justify-content to distribute), so the real content is
 * absolutely positioned over the ghost and vertically centered there instead.
 */
const twoLineValue = (primary, secondary) => (
    <Box sx={{ position: "relative" }}>
        <Box sx={{ visibility: "hidden" }} aria-hidden="true">
            <Box>{primary}</Box>
            <Box><Typography noWrap sx={{ fontSize: "12px" }}>&nbsp;</Typography></Box>
        </Box>
        {secondary ? (
            <Box sx={{ position: "absolute", inset: 0 }}>
                <Box>{primary}</Box>
                <Box sx={{ color: tokens.muted }}>
                    <Typography noWrap sx={{ fontSize: "12px" }}>{secondary}</Typography>
                </Box>
            </Box>
        ) : (
            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center" }}>
                {primary}
            </Box>
        )}
    </Box>
);

export default function DataWatcher(props) {
    const scans = useSelector((state) => state.scans);
    // A scan deleted here may be one Raw Surface Analysis previously saved from its currently-
    // loaded capture (both tabs stay mounted - see VisualizerContent's display:none toggling -
    // so RawSurfaceToolbar's own loadCaptureScans effect never re-fires just because a scan was
    // deleted elsewhere). Read here, not there, so "Compare Saved Scans" can't keep showing a
    // scan this delete just removed.
    const rawSurface = useSelector((state) => state.rawSurface);
    const {onDownloaded, onDataAvailable} = props;
    const dispatch = useDispatch();
    const [selectedAU, setSelectedAU] = useState(0);
    const [selectedScan, setSelectedScan] = useState(0);
    // Mirrors rawSurface.capturesStatus on the Raw Capture tab, but kept local: the scan list is
    // fetched here in getAUScans rather than by a thunk, so there is no slice to hang it off.
    const [scansLoading, setScansLoading] = useState(false);
    const [deleteScan, setDeleteScan] = useState({open: false, scanText: ""});
    // const auth = useSelector(state => state.auth);
    const {getAccessTokenSilently } = useAuth0();

    useEffect(() => {
        interval = setInterval(() => {
            if(selectedAU !== 0) {
                getAUScans(selectedAU, false);
            }
        }, 20000);

        return () => {
            clearInterval(interval);
        }
    });

    const handleSelected = async (id) => {
        if(onDataAvailable) {
            onDataAvailable();
        }

        let token = await getAccessTokenSilently();
        new api().request(`/datavisualizer/${id}`, token)
        .then(data => {
            if(onDownloaded) {
                onDownloaded(id, data);
            }
        })
        .catch(err => {
            console.log(err);
        });
    }

    const handleSelectAU = (evt) => {
        let auid = evt.target.value;
        setSelectedAU(auid);

        setSelectedScan(0);
        dispatch(resetData()); // unload the current scan

        if(auid !== 0) {
            getAUScans(auid);
        }
    }

    const getAUScans = async (auid, loadFirst) => {
        let token = await getAccessTokenSilently();
        setScansLoading(true);
        new api().request(`/scan/au/${auid}`, token)
        .then(data => {
            dispatch(loadScans({scans: data}));

            if(loadFirst) {
                handleSelected(data[0].scanId);
                setSelectedScan(data[0].scanId);
            }
        })
        .catch(err => {
            console.log(err);
        })
        .finally(() => {
            setScansLoading(false);
        });
    }

    // Same three states, in the same order, as RawSurfaceToolbar's captureFieldText.
    const scanFieldText = () => {
        if(scansLoading) return "Loading scans…";
        if(selectedAU !== 0 && scans.data.length === 0) return "No scans available";
        return "Select a scan";
    }

    const handleSelectScan = (evt) => {
        let scanId = evt.target.value;
        setSelectedScan(scanId);
        
        if(scanId !== 0) {
            handleSelected(scanId);
        }
    }

    const handleUploadClick = (evt) => {
        dispatch(uploadShow({auid: selectedAU}));
    }

    const handleFileUpload = () => {
        getAUScans(selectedAU, true);
    }

    const handleDeleteClick = () => {
        setDeleteScan({
            open: true,
            scanText: formatDateTime(scans.data.find(s=> s.scanId === selectedScan).endTime) 
        });
    }

    const handleCancel = () => {
        setDeleteScan({open: false, scanText: ""});
    }

    const handleDeleteScan = async () => {
        setDeleteScan({open: false, scanText: ""});

        dispatch(showProcessing({message: "Deleting scan..."}));
        let token = await getAccessTokenSilently();
        new api().request(`/scan/${selectedScan}`, token, "delete")
        .then(data => {
            console.log(`delete scan id: ${selectedScan}`);
            getAUScans(selectedAU); // refresh the list of scans
            setSelectedScan(0);
            dispatch(resetData()); // unload the current scan
            // Refresh Raw Surface's own saved-scan list too, in case the deleted scan was one
            // it saved from the currently-loaded capture - otherwise its "Compare Saved Scans"
            // button stays enabled against a list that no longer matches the server.
            if (rawSurface.captureId) {
                dispatch(loadCaptureScans({ captureId: rawSurface.captureId }));
            }
            dispatch(hideProcessing());
        })
        .catch(err => {
            console.log(err);
            dispatch(hideProcessing());
        });
    }

    const renderScans = () => {
        return (
            <>
                {/* Horizontal toolbar per the mockup - was previously a vertically-stacked card
                    sitting in the right rail. Delete Scan and Upload Scan each carry a hidden
                    label matching the field labels' height, so they start at the same row
                    position the dropdowns do, then center within FIELD_HEIGHT below that -
                    vertically centered against the dropdown itself, not the label+dropdown
                    column as a whole. */}
                <Stack direction="row" spacing={2.5} alignItems="flex-start" flexWrap="wrap">
                    <Box sx={{ minWidth: 220 }}>
                        <Typography sx={fieldLabelSx}>Alignment Unit</Typography>
                        <Select
                            fullWidth
                            size="small"
                            value={selectedAU}
                            onChange={handleSelectAU}
                            renderValue={(value) => {
                                const au = scans.au.find((a) => a.uniqueId === value);
                                return twoLineValue(au ? au.name : <em>Select an Alignment Unit</em>);
                            }}
                        >
                            {
                                (selectedAU === 0) && (<MenuItem value={0} key="select-au" ><em>Select an Alignment Unit</em></MenuItem>)
                            }
                            {scans.au.map((au, index) => {
                                return (
                                    <MenuItem
                                        value={au.uniqueId}
                                        key={`au-mi-${index}`}
                                        selected={selectedAU === au.uniqueId}
                                    >{au.name}
                                    </MenuItem>
                                )
                            })}
                        </Select>
                    </Box>

                    <Box sx={{ minWidth: 260 }}>
                        <Typography sx={fieldLabelSx}>Scan</Typography>
                        <Select
                            fullWidth
                            size="small"
                            disabled={selectedAU === 0}
                            value={selectedScan}
                            onChange={handleSelectScan}
                            renderValue={(value) => {
                                const scan = scans.data.find((s) => s.scanId === value);
                                return scan
                                    ? twoLineValue(formatDateTime(scan.endTime), scan.note)
                                    : twoLineValue(<em>{scanFieldText()}</em>);
                            }}
                        >
                            {
                                (selectedScan === 0) && (<MenuItem value={0} key="select-scan" ><em>{scanFieldText()}</em></MenuItem>)
                            }
                            {scans.data.map((scan, index) => {
                                return (
                                    <MenuItem
                                        value={scan.scanId}
                                        key={`mi-${index}`}
                                        selected={selectedScan === scan.scanId}
                                    >
                                        <Stack key={`s-${index}`}>
                                            <Box key={`b1-${index}`}>{`${formatDateTime(scan.endTime)}`}</Box>
                                            <Box key={`b2-${index}`} sx={{ color: tokens.muted }}>
                                                <Typography noWrap sx={{ fontSize: "12px" }}>
                                                    {`${scan.note}`}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </MenuItem>
                                )
                            })}
                        </Select>
                    </Box>

                    <Box>
                        <Typography sx={{ ...fieldLabelSx, visibility: "hidden" }}>Delete</Typography>
                        <Box sx={{ height: FIELD_HEIGHT, display: "flex", alignItems: "center" }}>
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                disabled={selectedAU === 0 || selectedScan === 0}
                                onClick={handleDeleteClick}
                            >
                                Delete Scan
                            </Button>
                        </Box>
                    </Box>

                    <Box sx={{ flexGrow: 1 }} />

                    <Box>
                        <Typography sx={{ ...fieldLabelSx, visibility: "hidden" }}>Upload</Typography>
                        <Box sx={{ height: FIELD_HEIGHT, display: "flex", alignItems: "center" }}>
                            <Button
                                variant="outlined"
                                color="inherit"
                                size="small"
                                disabled={selectedAU === 0}
                                onClick={handleUploadClick}
                            >
                                Upload Scan&hellip;
                            </Button>
                        </Box>
                    </Box>
                </Stack>

                <ScanUpload fileUploaded={handleFileUpload} />

                <Dialog open={deleteScan.open}>
                    <DialogContent>
                        <DialogTitle>Delete Scan</DialogTitle>
                        <DialogContentText>Please confirm you want to delete the scan from {deleteScan.scanText}. The data is unrecoverable once deleted.</DialogContentText>
                        <DialogActions>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={handleDeleteScan}
                            >Delete</Button>
                            <Button
                                variant="outlined"
                                color="inherit"
                                onClick={handleCancel}
                            >Cancel</Button>
                        </DialogActions>
                    </DialogContent>

                </Dialog>
            </>
        )
    }

    return (
        <Box id="data-watcher" sx={{
            backgroundColor: tokens.surface,
            borderBottom: `1px solid ${tokens.line}`,
            padding: "10px 20px",
        }}>
            {
                (scans.auStatus === "loading" || scans.auStatus === "idle") ?
                (
                    <>
                        <CircularProgress size={20} />
                        <span>Loading available Alignment Units...</span>
                    </>
                ) :
                (scans.auStatus === "error") ?
                (
                    <Alert severity="error">Error loading Alignment Units.</Alert>
                ) :
                (scans.au.length === 0) ?
                (
                    <Alert severity="info">No Alignment Units are available...</Alert>
                ) :
                (
                    renderScans()
                )
            }
        </Box>
    );
}